import React, { useState } from "react";
import api from "../../../../api/client";
import AssetModal, { Field } from "./AssetModal";
import { errorMessage, formatDuration, machineLabel, toInputDateTime } from "./assetUtils";

export function ReportBreakdownModal({ machines, machineId, onClose, onSaved }) {
  const reportable = machines.filter((m) => m.status !== "retired");
  const [form, setForm] = useState({
    machineId: machineId || "",
    description: "",
    startedAt: toInputDateTime(),
  });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const chosen = reportable.find((m) => m.id === form.machineId);

  const submit = async () => {
    if (!form.machineId) return setError("Choose the machine that has broken down.");
    setBusy(true);
    setError(null);
    try {
      const data = new FormData();
      data.append("machineId", form.machineId);
      data.append("description", form.description);
      data.append("startedAt", new Date(form.startedAt).toISOString());
      if (photo) data.append("file", photo);
      const res = await api.post("/manufacturing/assets/breakdowns", data);
      onSaved(res.data.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't report the breakdown."));
      setBusy(false);
    }
  };

  return (
    <AssetModal
      title="Report a breakdown"
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={submit}
      submitLabel="Report breakdown"
      submitClass="am-btn-danger"
    >
      <Field label="Machine *">
        <select className="am-select" value={form.machineId} onChange={set("machineId")} required>
          <option value="">— Choose a machine —</option>
          {reportable.map((m) => <option key={m.id} value={m.id}>{machineLabel(m)}{m.location ? ` · ${m.location}` : ""}</option>)}
        </select>
      </Field>
      {chosen?.openBreakdowns?.length > 0 && (
        <div className="am-hint">
          This machine is already reported as down. What you write is added to the open breakdown instead of opening a new one.
        </div>
      )}
      <Field label="What happened? *">
        <textarea className="am-textarea" value={form.description} onChange={set("description")} required maxLength={2000} placeholder="e.g. Spindle making a grinding noise and stopped mid-cycle" />
      </Field>
      <div className="am-row">
        <Field label="Stopped at" hint="Downtime is counted from here">
          <input className="am-input" type="datetime-local" value={form.startedAt} max={toInputDateTime()} onChange={set("startedAt")} required />
        </Field>
        <Field label="Photo (optional)">
          <input className="am-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setPhoto(e.target.files[0] || null)} />
        </Field>
      </div>
      <p className="am-muted" style={{ margin: 0 }}>
        The machine's technician is alerted on WhatsApp / Telegram. Workers can also report by sending <strong>/breakdown</strong> to the bot.
      </p>
    </AssetModal>
  );
}

export function ResolveBreakdownModal({ breakdown, onClose, onSaved }) {
  const [resolution, setResolution] = useState("");
  const [cost, setCost] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/manufacturing/assets/breakdowns/${breakdown.id}/resolve`, { resolution, cost });
      onSaved(res.data.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't resolve the breakdown."));
      setBusy(false);
    }
  };

  return (
    <AssetModal
      title={`Resolve ${breakdown.label}`}
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={submit}
      submitLabel="Mark as running"
    >
      <div className="am-hint">
        {machineLabel(breakdown.machine)} has been down for <strong>{formatDuration(breakdown.downtimeMinutes)}</strong>. Resolving stops the clock, marks the machine as running and files the fix in its maintenance history.
      </div>
      <Field label="What was done to fix it? *">
        <textarea className="am-textarea" value={resolution} onChange={(e) => setResolution(e.target.value)} required maxLength={2000} placeholder="e.g. Replaced spindle bearing, checked alignment" />
      </Field>
      <Field label="Repair cost (₹, optional)">
        <input className="am-input" type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} />
      </Field>
    </AssetModal>
  );
}
